/**
 * The route error boundary.
 *
 * Its job is to be a page. A render error in a single-page application leaves a white screen with
 * nothing on it — no header, no navigation, no indication that anything is still working — and
 * the only recovery a reader can find is the browser's back button. This renders the shell's
 * header, says what happened, and offers two ways out: reload, or go back to the catalog.
 *
 * `useRouteError` rather than a class component: React Router catches errors from loaders,
 * actions and rendering, and a hand-rolled `componentDidCatch` would only see the last of those.
 */
import { isRouteErrorResponse, useNavigate, useRouteError } from 'react-router';

import { Button, Container, EmptyState, Inline, Stack } from '@catalog/ui';

import { isApiError, messageFor } from '../api/client.js';
import { usePageMeta } from '../hooks/usePageMeta.js';

export function RootErrorBoundary() {
  const error = useRouteError();
  const navigate = useNavigate();

  const notFound = isRouteErrorResponse(error) ? error.status === 404 : false;

  const title = notFound ? 'That page does not exist' : 'Something went wrong';
  usePageMeta({ title, description: 'An unexpected error occurred in the HCLTech catalog.' });

  return (
    <Container size="md">
      <Stack gap="8" style={{ paddingBlock: 'var(--ex-space-12)' }}>
        <EmptyState
          headingLevel={2}
          title={title}
          description={describe(error, notFound)}
          actions={
            <Inline gap="3" justify="center">
              <Button
                variant="primary"
                onClick={() => {
                  void navigate('/courses');
                }}
              >
                Go to the catalog
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  /**
                   * A full reload, not a re-render.
                   *
                   * Whatever threw may have left the component tree in a state React cannot
                   * recover from, and a reader who has reached this page wants the app working
                   * again rather than a second chance at the same crash.
                   */
                  window.location.reload();
                }}
              >
                Reload the page
              </Button>
            </Inline>
          }
        />
      </Stack>
    </Container>
  );
}

function describe(error: unknown, notFound: boolean): string {
  if (notFound) {
    return 'The address may be mistyped, or the page may have moved. The catalog is still where you left it.';
  }

  if (isApiError(error)) return messageFor(error);

  if (isRouteErrorResponse(error)) {
    return `The page could not be shown (${String(error.status)} ${error.statusText}).`;
  }

  /**
   * The error's own message is not shown for an unknown throw.
   *
   * A stack-derived string is meaningless to a reader and may carry internals that are not theirs
   * to see. The real detail is in the browser console, where a developer will look.
   */
  return 'The page could not be shown. Reloading usually fixes it; if it does not, the catalog service may be having trouble.';
}
